import { useEffect, useRef, useState } from 'react';
import { clx } from '../../lib/utils';
import './ScrollShadow.styles.css';

interface IScrollShadowProps {
  children: React.ReactNode;
  innerClassName?: string;
  outerClassName?: string;
}

export const ScrollShadow = ({ children, innerClassName, outerClassName }: IScrollShadowProps) => {
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const [isAtTop, setIsAtTop] = useState(false);
  const [isAtBottom, setIsAtBottom] = useState(false);

  const isMounted = useRef(false);
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const handleScroll = () => {
      if (!isMounted.current) return;
      setIsAtTop(el.scrollTop === 0);
      setIsAtBottom(el.scrollTop + el.clientHeight >= el.scrollHeight);
    };

    el.addEventListener('scroll', handleScroll);
    handleScroll(); // Initial check

    // Re-check on resize
    const resizeObserver = new ResizeObserver(handleScroll);
    resizeObserver.observe(el);

    return () => {
      el.removeEventListener('scroll', handleScroll);
      resizeObserver.unobserve(el);
    };
  }, []);

  return (
    <div className={clx('relative flex min-h-0 w-full shrink flex-col', outerClassName)}>
      <div ref={scrollRef} className={clx('min-h-0 shrink overflow-y-auto', innerClassName)}>
        {children}
      </div>
      <div
        className={clx(
          'scrollShadow-top pointer-events-none transition-opacity',
          isAtTop ? 'opacity-0' : 'opacity-100'
        )}
      ></div>
      <div
        className={clx(
          'scrollShadow-bottom pointer-events-none transition-opacity',
          isAtBottom ? 'opacity-0' : 'opacity-100'
        )}
      ></div>
    </div>
  );
};
